import queryClient from '@src/app/providers/queryClient';
import type { APIClient } from '@src/shared/api';
import useAPIClient from '@src/shared/hooks/api/useAPIClient';
import { buildSectionCommitFormData } from '@src/shared/lib';
import { useMutation } from '@src/shared/query';
import {
  type InfiniteData,
  keepPreviousData,
  queryOptions,
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query';

import {
  clientDetailFromDto,
  clientFilterOptionsFromDto,
  clientGroupListItemFromDto,
  clientOptionFromDto,
  clientOptionsFromDto,
} from '../../lib';
import type {
  BusinessSegment,
  BusinessSegmentChoicesResponse,
  ClientDetail,
  ClientDetailDto,
  ClientFilterOptions,
  ClientFilterOptionsDto,
  ClientGroupOption,
  ClientListFilterParams,
  ClientListOrdering,
  ClientListResponse,
  ClientListResponseDto,
  ClientOption,
  ClientOptionDto,
  ClientOptions,
  ClientOptionsDto,
  ClientSummary,
  CommitClientDetailPayload,
  CommitClientDetailResponse,
} from '../../types';
import {
  businessSegmentsUrl,
  clientAutocompleteUrl,
  clientCompanyNamesUrl,
  clientFilterOptionsUrl,
  clientGroupAutocompleteUrl,
  clientOptionsUrl,
  clientSummaryUrl,
  clientsUrl,
  clientUrl,
} from './constants';
import { CLIENTS_KEY, clientDetailKey, invalidateClients } from './keys';

/** Lookup tables of the module: sectors, statuses, service areas, ... */
export function useClientOptions() {
  const apiClient = useAPIClient();
  return useQuery({
    queryKey: [...CLIENTS_KEY, 'options'],
    queryFn: ({ signal }) =>
      apiClient.request({
        url: clientOptionsUrl,
        options: { signal },
      }) as Promise<ClientOptionsDto>,
    select: (data): ClientOptions => clientOptionsFromDto(data),
    staleTime: Number.POSITIVE_INFINITY,
  });
}

const NO_SEGMENT_LABELS: BusinessSegmentChoicesResponse = {};

/**
 * Translated labels of both business segments; none until they are loaded.
 * Not `initialData`: with an infinite `staleTime` it would count as loaded
 * and the labels would never be fetched.
 */
export function useBusinessSegments() {
  const apiClient = useAPIClient();
  const query = useQuery({
    queryKey: [...CLIENTS_KEY, 'business-segments'],
    queryFn: ({ signal }) =>
      apiClient.request({
        url: businessSegmentsUrl,
        options: { signal },
      }) as Promise<BusinessSegmentChoicesResponse>,
    staleTime: Number.POSITIVE_INFINITY,
  });
  return { ...query, data: query.data ?? NO_SEGMENT_LABELS };
}

type UseClientGroupsParams = {
  pageSize: number;
  ordering?: ClientListOrdering | `-${ClientListOrdering}`;
  filterParams: ClientListFilterParams;
  enabled?: boolean;
};

/** Clients list: groups with their clients nested, one page at a time. */
export function useClientGroups({
  pageSize,
  ordering,
  filterParams,
  enabled = true,
}: UseClientGroupsParams) {
  const apiClient = useAPIClient();
  return useInfiniteQuery({
    queryKey: [...CLIENTS_KEY, 'list', pageSize, ordering, filterParams],
    enabled,
    placeholderData: keepPreviousData,
    initialPageParam: 1,
    queryFn: ({ pageParam, signal }) =>
      apiClient.request({
        url: clientsUrl,
        payload: {
          ...filterParams,
          page: String(pageParam),
          page_size: String(pageSize),
          ...(ordering ? { ordering } : {}),
        },
        options: { signal },
      }) as Promise<ClientListResponseDto>,
    select: (data): InfiniteData<ClientListResponse> => ({
      ...data,
      pages: data.pages.map((page) => ({
        ...page,
        results: page.results.map(clientGroupListItemFromDto),
      })),
    }),
    getNextPageParam: (lastPage) =>
      lastPage.meta.next ? lastPage.meta.page + 1 : undefined,
  });
}

/** Total, active and inactive clients the user may view. */
export function useClientSummary() {
  const apiClient = useAPIClient();
  return useQuery({
    queryKey: [...CLIENTS_KEY, 'summary'],
    queryFn: ({ signal }) =>
      apiClient.request({
        url: clientSummaryUrl,
        options: { signal },
      }) as Promise<ClientSummary>,
  });
}

/** Values of the Seat, Manager and Coordinator filters of the list. */
export function useClientFilterOptions() {
  const apiClient = useAPIClient();
  return useQuery({
    queryKey: [...CLIENTS_KEY, 'filter-options'],
    queryFn: ({ signal }) =>
      apiClient.request({
        url: clientFilterOptionsUrl,
        options: { signal },
      }) as Promise<ClientFilterOptionsDto>,
    select: (data): ClientFilterOptions => clientFilterOptionsFromDto(data),
  });
}

function clientDetailQueryOptions(apiClient: APIClient, id: number | string) {
  return queryOptions({
    queryKey: clientDetailKey(id),
    queryFn: ({ signal }) =>
      apiClient.request({
        url: clientUrl(id),
        options: { signal },
      }) as Promise<ClientDetailDto>,
  });
}

/** Header and General section of the client page. */
export function useClientDetail(
  id: number | string,
  { enabled = true }: { enabled?: boolean } = {},
) {
  const apiClient = useAPIClient();
  return useQuery({
    ...clientDetailQueryOptions(apiClient, id),
    enabled,
    select: (data): ClientDetail => clientDetailFromDto(data),
  });
}

/**
 * The client of the client page outside React, e.g. in a route loader.
 * Shares the cache with `useClientDetail`.
 */
export async function fetchClientDetail(
  apiClient: APIClient,
  id: number | string,
): Promise<ClientDetail> {
  const data = await queryClient.ensureQueryData(
    clientDetailQueryOptions(apiClient, id),
  );
  return clientDetailFromDto(data);
}

/** Saves the given sections of the client page together. */
export function useCommitClientDetail() {
  return useMutation<
    CommitClientDetailResponse,
    Error,
    { id: number | string; sections: CommitClientDetailPayload }
  >({
    mutationKey: [...CLIENTS_KEY, 'detail', 'commit'],
    mutateFn: ({ apiClient, id, sections }) =>
      apiClient.request({
        url: `${clientUrl(id)}detail/commit/`,
        method: 'PATCH',
        payload: buildSectionCommitFormData(sections),
        contentType: 'multipart/form-data',
      }) as Promise<CommitClientDetailResponse>,
    onSuccess: (response, variables) => {
      // Every saved section answers with the whole client; it goes into the
      // cache at once, so the page does not show the old one meanwhile.
      const saved = Object.values(response.results).find(Boolean);
      if (saved) {
        queryClient.setQueryData(clientDetailKey(variables.id), saved);
      }
      void invalidateClients();
    },
  });
}

type UseClientAutocompleteParams = {
  q: string;
  segment?: BusinessSegment;
  groupId?: number;
  enabled?: boolean;
};

/** Up to 20 clients whose internal or legal name contains `q`. */
export function useClientAutocomplete({
  q,
  segment,
  groupId,
  enabled = true,
}: UseClientAutocompleteParams) {
  const apiClient = useAPIClient();
  return useQuery({
    queryKey: [...CLIENTS_KEY, 'autocomplete', q, segment, groupId],
    enabled,
    placeholderData: keepPreviousData,
    queryFn: ({ signal }) =>
      apiClient.request({
        url: clientAutocompleteUrl,
        payload: {
          q,
          ...(segment ? { segment } : {}),
          ...(groupId ? { group: String(groupId) } : {}),
        },
        options: { signal },
      }) as Promise<ClientOptionDto[]>,
    select: (data): ClientOption[] => data.map(clientOptionFromDto),
  });
}

/**
 * Up to 20 group, internal and legal names that contain `q`: suggestions of
 * the Company filter of the list.
 */
export function useClientCompanyNames({
  q,
  enabled = true,
}: {
  q: string;
  enabled?: boolean;
}) {
  const apiClient = useAPIClient();
  return useQuery({
    queryKey: [...CLIENTS_KEY, 'company-names', q],
    enabled,
    placeholderData: keepPreviousData,
    queryFn: ({ signal }) =>
      apiClient.request({
        url: clientCompanyNamesUrl,
        payload: { q },
        options: { signal },
      }) as Promise<string[]>,
  });
}

/**
 * Up to 20 client groups the user may view whose name contains `q`; with
 * `forNewClient`, any group, also one with clients of another segment only.
 */
export function useClientGroupAutocomplete({
  q,
  forNewClient = false,
  enabled = true,
}: {
  q: string;
  forNewClient?: boolean;
  enabled?: boolean;
}) {
  const apiClient = useAPIClient();
  return useQuery({
    queryKey: [...CLIENTS_KEY, 'group-autocomplete', q, forNewClient],
    enabled,
    placeholderData: keepPreviousData,
    queryFn: ({ signal }) =>
      apiClient.request({
        url: clientGroupAutocompleteUrl,
        payload: {
          ...(q ? { q } : {}),
          ...(forNewClient ? { for_new_client: 1 } : {}),
        },
        options: { signal },
      }) as Promise<ClientGroupOption[]>,
  });
}
